import React, { useState } from 'react';
import { X } from 'lucide-react';
import { auth, db } from '@/config/firebase';
import { collection, addDoc, doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { Button } from '@common/Button';
import { Input, Select } from '@common/FormFields';
import { VEHICLE_TYPES, VEHICLE_TYPES_WITH_PLACEHOLDER } from "@/constants/vehicleTypes";
import { validateSurvey } from "../../utils/validation/surveyValidation";
import { logError, logInfo } from "@utils/logger";
import { generateSurveyId } from "@utils/idGenerators";

export const SurveyForm = ({ 
  survey = null, 
  isEdit = false, 
  onClose, 
  onSave,
  refetch = () => {} // Provide default empty function
}) => {
  const [formData, setFormData] = useState({
    name: survey?.name || '',
    customerName: survey?.customerName || '',
    customerPO: survey?.customerPO || '',
    mapInfo: survey?.mapInfo || '',
    surveyType: survey?.surveyType || '',
    madeBy: survey?.madeBy || '',
    validatedBy: survey?.validatedBy || '',
    transportCompany: survey?.transportCompany || '',
    challenges: survey?.challenges || '',
    stakeholderFeedback: survey?.stakeholderFeedback || '',
    additionalNotes: survey?.additionalNotes || '',
    origin: survey?.origin || '',
    destination: survey?.destination || '',
    vehicle: {
      type: survey?.vehicle?.type || '',
      length: survey?.vehicle?.length || '',
      width: survey?.vehicle?.width || '',
      height: survey?.vehicle?.height || '',
      weight: survey?.vehicle?.weight || '',
      weightUnit: survey?.vehicle?.weightUnit || 'lbs'
    },
    highPoleHeights: {
      required: survey?.highPoleHeights?.required || false,
      value: survey?.highPoleHeights?.value || '',
      unit: survey?.highPoleHeights?.unit || 'ft'
    },
    notes: survey?.notes || ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);
    setError('');

    try {
      // Validate form data
      const validationErrors = validateSurvey(formData);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setLoading(false);
        return;
      }

      if (!auth.currentUser) {
        throw new Error('You must be logged in to create a survey');
      }

      if (isEdit && survey) {
        // Check if document exists before updating
        const surveyRef = doc(db, 'surveys', survey.id);
        const surveyDoc = await getDoc(surveyRef);
        
        if (!surveyDoc.exists()) {
          // If document doesn't exist, create it
          await setDoc(surveyRef, {
            ...formData,
            id: survey.id,
            ownerId: auth.currentUser.uid,
            ownerEmail: auth.currentUser.email,
            status: 'draft',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
          logInfo('Survey created successfully');
        } else {
          // Update existing survey
          await updateDoc(surveyRef, {
            ...formData,
            updatedAt: new Date().toISOString()
          });
          logInfo('Survey updated successfully');
        }
      } else {
        // Create new survey
        const surveyId = generateSurveyId();
        const newSurvey = {
          ...formData,
          id: surveyId,
          ownerId: auth.currentUser.uid,
          ownerEmail: auth.currentUser.email,
          status: 'draft',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const surveysRef = collection(db, 'surveys');
        const docRef = await addDoc(surveysRef, newSurvey);
        // Add the document ID to the survey object for reference
        newSurvey.firestoreId = docRef.id;
        logInfo('Survey created successfully');
        logInfo('New survey ID:', surveyId, 'Firestore ID:', docRef.id);
      }

      // Call onSave callback with form data
      await onSave?.(formData);
      
      // Refetch surveys to update the list
      if (refetch) {
        await refetch();
      }
      
      onClose();
    } catch (err) {
      logError('Failed to save survey:', err);
      setError('Failed to save survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-[400] p-4 overflow-y-auto">
      <div className="bg-[#2A2B2E] rounded-lg w-full max-w-2xl my-8">
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {isEdit ? 'Edit Survey' : 'New Survey'}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Form - Scrollable */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <Input
              label="Survey Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Input
              label="Customer Name"
              value={formData.customerName}
              onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Input
              label="Customer PO"
              value={formData.customerPO}
              onChange={(e) => setFormData(prev => ({ ...prev, customerPO: e.target.value }))}
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />
          </div>

          {/* Map Info Box */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Map Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Map Details
                <span className="text-xs text-gray-400 ml-2">Supports Markdown</span>
              </label>
              <textarea
                value={formData.mapInfo}
                onChange={(e) => setFormData(prev => ({ ...prev, mapInfo: e.target.value }))}
                className="w-full h-40 bg-gray-700 border border-gray-600 rounded-lg p-3 text-white resize-none font-mono"
                placeholder="Enter map information and details..."
              />
              <p className="text-xs text-gray-400 mt-1">
                Use Markdown for formatting: **bold**, *italic*, - for lists, # for headings
              </p>
            </div>
          </div>

          {/* Survey Type and Metadata */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Survey Information</h3>
            
            <Select
              label="Survey Type"
              value={formData.surveyType}
              onChange={(value) => setFormData(prev => ({ ...prev, surveyType: value }))}
              options={[
                { value: 'book_survey', label: 'Book Survey' },
                { value: 'high_pole_survey', label: 'High Pole Survey' },
                { value: 'laser_survey', label: 'Laser Survey' },
                { value: 'lidar_survey', label: 'LiDAR Survey' },
                { value: 'site_survey', label: 'Site Survey' },
                { value: 'bridge_survey', label: 'Bridge Survey' }
              ]}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Survey Made By"
                value={formData.madeBy}
                onChange={(e) => setFormData(prev => ({ ...prev, madeBy: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />

              <Input
                label="Survey Validated By"
                value={formData.validatedBy}
                onChange={(e) => setFormData(prev => ({ ...prev, validatedBy: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
            </div>

            <Input
              label="Transport Company"
              value={formData.transportCompany}
              onChange={(e) => setFormData(prev => ({ ...prev, transportCompany: e.target.value }))}
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
              placeholder="Enter transport company name if known"
            />
          </div>

          {/* Challenges and Stakeholder Feedback */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Challenges & Feedback</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Challenges Identified
                <span className="text-xs text-gray-400 ml-2">Supports Markdown</span>
              </label>
              <textarea
                value={formData.challenges}
                onChange={(e) => setFormData(prev => ({ ...prev, challenges: e.target.value }))}
                className="w-full h-32 bg-gray-700 border border-gray-600 rounded-lg p-3 text-white resize-none font-mono"
                placeholder="List potential issues and recommendations..."
              />
              <p className="text-xs text-gray-400 mt-1">
                Use Markdown for formatting: **bold**, *italic*, - for lists
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Stakeholder Feedback
                <span className="text-xs text-gray-400 ml-2">Supports Markdown</span>
              </label>
              <textarea
                value={formData.stakeholderFeedback}
                onChange={(e) => setFormData(prev => ({ ...prev, stakeholderFeedback: e.target.value }))}
                className="w-full h-32 bg-gray-700 border border-gray-600 rounded-lg p-3 text-white resize-none font-mono"
                placeholder="Input from local authorities or other parties..."
              />
            </div>
          </div>

          {/* Additional Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Additional Notes</h3>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Notes
                <span className="text-xs text-gray-400 ml-2">Supports Markdown</span>
              </label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                className="w-full h-32 bg-gray-700 border border-gray-600 rounded-lg p-3 text-white resize-none font-mono"
                placeholder="Add any additional notes or comments..."
              />
              <p className="text-xs text-gray-400 mt-1">
                Use Markdown for formatting: **bold**, *italic*, - for lists, # for headings
              </p>
            </div>
          </div>

          {/* Route Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Route Information</h3>
            
            <Input
              label="Origin"
              value={formData.origin}
              onChange={(e) => setFormData(prev => ({ ...prev, origin: e.target.value }))}
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />

            <Input
              label="Destination"
              value={formData.destination}
              onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />
          </div>

          {/* Vehicle Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Vehicle Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Vehicle Type"
                value={formData.vehicle.type}
                onChange={(value) => setFormData(prev => ({
                  ...prev,
                  vehicle: { ...prev.vehicle, type: value }
                }))}
                options={VEHICLE_TYPES_WITH_PLACEHOLDER}
                required
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Weight"
                  type="number"
                  value={formData.vehicle?.weight || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    vehicle: { ...prev.vehicle, weight: e.target.value }
                  }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  labelClassName="text-gray-300"
                />
                <Select
                  label="Weight Unit"
                  value={formData.vehicle?.weightUnit || 'lbs'}
                  onChange={(value) => setFormData(prev => ({
                    ...prev,
                    vehicle: { ...prev.vehicle, weightUnit: value }
                  }))}
                  options={[
                    { value: 'lbs', label: 'lbs' },
                    { value: 'kg', label: 'kg' }
                  ]}
                  className="bg-gray-700 border-gray-600 text-white"
                  labelClassName="text-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Length (ft)"
                type="number"
                value={formData.vehicle.length}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  vehicle: { ...prev.vehicle, length: e.target.value }
                }))}
                required={!!formData.vehicle.type}
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />

              <Input
                label="Width (ft)"
                type="number"
                value={formData.vehicle.width}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  vehicle: { ...prev.vehicle, width: e.target.value }
                }))}
                required={!!formData.vehicle.type}
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />

              <Input
                label="Height (ft)"
                type="number"
                value={formData.vehicle.height}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  vehicle: { ...prev.vehicle, height: e.target.value }
                }))}
                required={!!formData.vehicle.type}
                className="bg-gray-700 border-gray-600 text-white"
                labelClassName="text-gray-300"
              />
            </div>
          </div>

          {/* High Pole Requirements */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">High Pole Requirements</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.highPoleHeights?.required || false}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      highPoleHeights: {
                        ...prev.highPoleHeights,
                        required: e.target.checked
                      }
                    }))}
                    className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
                  />
                  <span className="text-gray-300">Required</span>
                </label>
              </div>
              {formData.highPoleHeights?.required && (
                <>
                  <Input
                    label="Height"
                    type="number"
                    value={formData.highPoleHeights?.value || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      highPoleHeights: {
                        ...prev.highPoleHeights,
                        value: e.target.value
                      }
                    }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    labelClassName="text-gray-300"
                  />
                  <Select
                    label="Unit"
                    value={formData.highPoleHeights?.unit || 'ft'}
                    onChange={(value) => setFormData(prev => ({
                      ...prev,
                      highPoleHeights: {
                        ...prev.highPoleHeights,
                        unit: value
                      }
                    }))}
                    options={[
                      { value: 'ft', label: 'feet' },
                      { value: 'm', label: 'meters' }
                    ]}
                    className="bg-gray-700 border-gray-600 text-white"
                    labelClassName="text-gray-300"
                  />
                </>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Additional Notes</h3>
            
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full h-32 bg-gray-700 border border-gray-600 rounded-lg p-3 text-white resize-none"
              placeholder="Add any additional notes or comments..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Survey' : 'Create Survey'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};